import { useEffect, useRef } from 'react';
import { apiGet } from '../api';
import { unwrapData } from '@wapharma/shared';
import { getToken } from '../auth';
import { useAuth } from '../contexts/AuthContext';
import { useInboxCount } from '../contexts/InboxCountContext';
import { inboxItemKey, type InboxItem } from './useInboxListState';
import { API_BASE } from '../lib/apiBase';
import { playNotificationSound } from '../lib/notificationSound';
import { splitSseEvents, sseEventData } from '../lib/sse';

const POLL_MS = 20_000;
const STREAM_RECONNECT_MAX_MS = 30_000;

type InboxListResponse = {
  data: InboxItem[];
  meta?: { unseen?: number; total?: number };
};

function unseenKeys(items: InboxItem[]): Set<string> {
  const out = new Set<string>();
  for (const item of items) {
    if (!item.seen) out.add(inboxItemKey(item));
  }
  return out;
}

/**
 * Live inbox: SSE push (`GET /inbox/stream`) triggers an immediate refetch;
 * the 20s poll stays as fallback when the stream is down. Badge + sound +
 * reload event, no full page refresh.
 */
export function useInboxLivePoll() {
  const { user } = useAuth();
  const { syncCounts } = useInboxCount();
  const prevUnseenRef = useRef<Set<string> | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!user) {
      prevUnseenRef.current = null;
      initializedRef.current = false;
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const poll = async () => {
      const token = getToken();
      if (!token || cancelled || document.hidden) return;
      try {
        const body = await apiGet<InboxListResponse>('/inbox', token);
        if (cancelled) return;
        const list = unwrapData<InboxItem[]>(body);
        const meta = body.meta;
        const unseen =
          typeof meta?.unseen === 'number' ? meta.unseen : list.filter((i) => !i.seen).length;
        syncCounts({ total: list.length, unseen });

        const nextUnseen = unseenKeys(list);
        if (!initializedRef.current) {
          initializedRef.current = true;
          prevUnseenRef.current = nextUnseen;
          return;
        }

        let hasNew = false;
        for (const key of nextUnseen) {
          if (!prevUnseenRef.current?.has(key)) {
            hasNew = true;
            break;
          }
        }
        prevUnseenRef.current = nextUnseen;

        if (hasNew) {
          playNotificationSound();
          window.dispatchEvent(new Event('wapharma-inbox-reload'));
        }
      } catch {
        /* silent — next tick */
      }
    };

    const start = () => {
      if (timer || document.hidden) return;
      timer = setInterval(() => void poll(), POLL_MS);
    };

    // SSE push — fetch-streamed because EventSource cannot send the Bearer header.
    let streamAbort: AbortController | null = null;
    let reconnectTimer: number | null = null;
    let streamAttempt = 0;

    const connectStream = async () => {
      const token = getToken();
      if (!token || cancelled) return;
      const controller = new AbortController();
      streamAbort = controller;
      try {
        const res = await fetch(`${API_BASE}/inbox/stream`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          credentials: 'include',
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`sse_status_${res.status}`);
        streamAttempt = 0;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const { events, rest } = splitSseEvents(buf);
          buf = rest;
          for (const block of events) {
            if (sseEventData(block) === 'changed') void poll();
          }
        }
      } catch {
        /* reconnect below */
      }
      if (cancelled || controller.signal.aborted) return;
      streamAttempt += 1;
      const delayMs = Math.min(STREAM_RECONNECT_MAX_MS, 1_000 * 2 ** Math.min(streamAttempt, 5));
      reconnectTimer = window.setTimeout(() => void connectStream(), delayMs);
    };

    void poll();
    start();
    void connectStream();

    const onVisibility = () => {
      if (document.hidden) {
        stop();
        return;
      }
      void poll();
      start();
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      stop();
      streamAbort?.abort();
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, syncCounts]);
}
